import { addDoc, collection, deleteDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { initialStageMap } from '@/lib/stages'
import type { Incoterms } from '@/types/project'

export interface NewProjectInput {
  name: string
  contractNumber: string
  productName: string
  productQuantity: string
  incoterms: Incoterms
  deliveryLocation: string
  originLocation: string
  supplierUid: string
  supplierName: string
  clientUid: string
  clientName: string
}

function normalizeOrigin(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/** Editable business fields only (server-owned fields are never sent by the client). */
function businessFields(input: NewProjectInput) {
  return {
    name: input.name.trim(),
    contractNumber: input.contractNumber.trim(),
    productName: input.productName.trim(),
    productQuantity: input.productQuantity.trim(),
    incoterms: input.incoterms,
    deliveryLocation: input.deliveryLocation.trim(),
    originLocation: normalizeOrigin(input.originLocation),
    supplierUid: input.supplierUid,
    supplierName: input.supplierName,
    clientUid: input.clientUid,
    clientName: input.clientName,
  }
}

/**
 * Build a new project write payload with the correct initial invariants
 * (blueprint §4.3): 10 Red stages, greenCount 0, Ongoing, geo pending,
 * server-generated startDate.
 */
export function buildNewProject(input: NewProjectInput, createdBy: string) {
  return {
    ...businessFields(input),
    geo: { delivery: null, origin: null, status: 'pending', geocodedAt: null },
    status: 'ongoing',
    startDate: serverTimestamp(),
    completionDate: null,
    durationDays: null,
    stages: initialStageMap(),
    greenCount: 0,
    createdAt: serverTimestamp(),
    createdBy,
  }
}

export async function createProject(input: NewProjectInput, createdBy: string): Promise<void> {
  await addDoc(collection(db, 'projects'), buildNewProject(input, createdBy))
}

export async function updateProject(id: string, input: NewProjectInput): Promise<void> {
  await updateDoc(doc(db, 'projects', id), businessFields(input))
}

export async function deleteProject(id: string): Promise<void> {
  await deleteDoc(doc(db, 'projects', id))
}
